import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, ShoppingBag, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import OrderForm from "@/components/OrderForm";
import api, { fmt, fmtDate, openInNewTab } from "@/lib/api";

const STATUS_FILTERS = ["All", "New", "With Karigar", "Ready", "In Shop", "Delivered", "Cancelled"];

const QUICK_FILTERS = [
  { key: "due-today", label: "Today's Deliveries" },
  { key: "due-tomorrow", label: "Tomorrow Deliveries" },
  { key: "overdue", label: "Overdue Orders" },
  { key: "high-value", label: "High Value Orders" },
  { key: "partial", label: "Partial Payments" },
  { key: "paid", label: "Completed Payments" },
];

const isoToday = () => new Date().toLocaleDateString("en-CA");
const isoTomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString("en-CA"); };

const daysLeft = (o) => {
  if (!o.delivery_date || ["Delivered", "Cancelled"].includes(o.status)) return null;
  const diff = Math.round((new Date(o.delivery_date) - new Date(isoToday())) / 86400000);
  if (diff < 0) return { label: "Overdue", cls: "text-red-500 font-medium" };
  if (diff === 0) return { label: "Today", cls: "text-amber-500 font-medium" };
  return { label: `${diff} Day${diff > 1 ? "s" : ""} Left`, cls: diff <= 2 ? "text-orange-400" : "text-muted-foreground" };
};

const matchQuick = (o, quick) => {
  const active = !["Delivered", "Cancelled"].includes(o.status);
  switch (quick) {
    case "due-today": return o.delivery_date === isoToday() && active;
    case "due-tomorrow": return o.delivery_date === isoTomorrow() && active;
    case "overdue": return o.delivery_date && o.delivery_date < isoToday() && active;
    case "high-value": return (o.total_amount || 0) >= 10000;
    case "partial": return o.payment_status === "Partial";
    case "paid": return o.payment_status === "Paid";
    case "with-karigar": return o.status === "With Karigar";
    default: return true;
  }
};

export default function Orders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("All");
  const quick = searchParams.get("filter") || "";

  const setQuick = (key) => setSearchParams(key ? { filter: key } : {}, { replace: true });

  const { data: orders, isLoading } = useQuery({ queryKey: ["orders"], queryFn: async () => (await api.get("/orders")).data });

  const filtered = orders?.filter((o) => (filter === "All" || o.status === filter) && (!quick || matchQuick(o, quick))) || [];

  const openPdf = (e, o) => {
    e.stopPropagation();
    openInNewTab(async () => {
      const a = (o.attachments || [])[0];
      const res = await api.get(`/orders/${o.id}/attachments/${a.id}`, { responseType: "blob" });
      return URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    }).catch((err) => {
      console.error("Failed to open attachment PDF", err);
      toast.error("PDF open nahi ho paya");
    });
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">{orders?.length || 0} customer orders</p>
        </div>
        <Button data-testid="add-order-button" className="rounded-full active:scale-95 transition-transform" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> New Order
        </Button>
        <OrderForm open={open} onOpenChange={setOpen} />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            data-testid={`order-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${filter === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:text-foreground"}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="quick-filters">
        {QUICK_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`quick-filter-${f.key}`}
            onClick={() => setQuick(quick === f.key ? "" : f.key)}
            className={`px-3.5 py-1 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${quick === f.key ? "bg-amber-500/15 text-amber-500 border-amber-500/40" : "bg-card text-muted-foreground hover:text-foreground"}`}
          >
            {f.label}
          </button>
        ))}
        {quick === "with-karigar" && (
          <span className="px-3.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-500 border border-amber-500/40">With Karigar</span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-14 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <ShoppingBag className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-3 font-medium">No matching orders</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Karigar</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Days Left</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead className="text-center">PDF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((o) => {
                const dl = daysLeft(o);
                return (
                  <TableRow key={o.id} data-testid={`order-row-${o.order_number}`} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate(`/orders/${o.id}`)}>
                    <TableCell className="font-medium">{o.order_number}</TableCell>
                    <TableCell>{o.customer_name}</TableCell>
                    <TableCell className="text-muted-foreground">{o.customer_phone || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{(o.karigar_names || []).join(", ") || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(o.delivery_date)}</TableCell>
                    <TableCell>{dl ? <span className={dl.cls}>{dl.label}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell><StatusBadge status={o.payment_status} /></TableCell>
                    <TableCell className="text-right">{fmt(o.total_amount)}</TableCell>
                    <TableCell className={`text-right ${o.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{fmt(o.profit)}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(o.remaining)}</TableCell>
                    <TableCell className="text-center">
                      {o.attachments_count > 0 ? (
                        <button data-testid={`pdf-icon-${o.order_number}`} title={`${o.attachments_count} PDF(s)`} className="inline-flex items-center gap-1 text-amber-500 hover:text-amber-400" onClick={(e) => openPdf(e, o)}>
                          <Paperclip className="h-4 w-4" />{o.attachments_count > 1 && <span className="text-xs">{o.attachments_count}</span>}
                        </button>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
